import { useEffect, useMemo, useState } from 'react';
import { Loader2, Settings } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import {
  getClientTracker,
  type ClientTrackerResponse,
} from '../../../services/admin/clientAnalyticsService';
import { CcStartDateModal } from './analytics/CcStartDateModal';
import { CostMatrixView } from './analytics/CostMatrixView';
import { RevenueMatrixView } from './analytics/RevenueMatrixView';
import { SpendingMatrixView } from './analytics/SpendingMatrixView';
import { OverviewView } from './analytics/OverviewView';
import {
  buildColumns,
  sumRestaurants,
  costPctRatio,
  formatPct,
  formatEuro,
  type Granularity,
  type Lens,
} from './analytics/trackerUtils';

type View = 'cost' | 'revenue' | 'spending' | 'overview';

const LENSES: { key: Lens; label: string }[] = [
  { key: 'total', label: 'Total' },
  { key: 'food', label: 'Food' },
  { key: 'drinks', label: 'Drinks' },
];

const VIEWS: { key: View; label: string }[] = [
  { key: 'cost', label: 'Cost %' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'spending', label: 'Spending' },
  { key: 'overview', label: 'Overview' },
];

const AdminAnalyticsPage = () => {
  const [data, setData] = useState<ClientTrackerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lens, setLens] = useState<Lens>('total');
  // Granularity is fixed to "Months + Q" — the quarter-only view was removed.
  const granularity: Granularity = 'months';
  const [view, setView] = useState<View>('cost');
  const [showCcModal, setShowCcModal] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getClientTracker();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const allRestaurants = useMemo(
    () => (data ? data.clients.flatMap((c) => c.restaurants) : []),
    [data]
  );
  const columns = useMemo(
    () => (data ? buildColumns(data.months, granularity) : []),
    [data, granularity]
  );

  const summary = useMemo(() => {
    if (!data) return null;
    const pooled = sumRestaurants(allRestaurants, data.months, lens);
    return {
      omzet: pooled.omzet,
      inslag: pooled.inslag,
      costPct: costPctRatio(pooled),
      clients: data.clients.length,
      restaurantsWithData: allRestaurants.filter((r) => r.monthsFilled > 0).length,
    };
  }, [data, allRestaurants, lens]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className={cn(primaryText, 'text-2xl', semiBoldText)}>Client Data Tracker</h2>
            <p className={cn(secondaryText, 'mt-1', smallTextSize)}>
              Omzet, inslag and cost % (inslag ÷ omzet) per client and restaurant, computed from
              order &amp; revenue data. Total cost % includes non-food spend; Food/Drinks exclude it,
              so they don't sum to Total. Before/after is measured on total cost % around each
              client's CC start date.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Lens toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {LENSES.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setLens(l.key)}
                  className={cn(
                    smallTextSize,
                    'px-3 py-1 rounded-md transition-colors',
                    lens === l.key
                      ? 'bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-300 font-medium shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowCcModal(true)}
              disabled={!data}
              title="Edit CC start dates"
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-lg',
                'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700',
                'text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-50',
                smallTextSize
              )}
            >
              <Settings className="w-4 h-4" />
              CC dates
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className={smallTextSize}>Loading analytics…</span>
          </div>
        )}

        {error && !loading && (
          <div className={cn(cardClass, 'p-4 border-red-300 dark:border-red-700')}>
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && data && summary && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SummaryCard label={`Omzet (${lens})`} value={formatEuro(summary.omzet)} />
              <SummaryCard label={`Inslag (${lens})`} value={formatEuro(summary.inslag)} />
              <SummaryCard label={`Blended cost % (${lens})`} value={formatPct(summary.costPct)} />
              <SummaryCard label="Clients" value={String(summary.clients)} />
              <SummaryCard label="Restaurants w/ data" value={String(summary.restaurantsWithData)} />
            </div>

            {/* View tabs */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
                {VIEWS.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => setView(v.key)}
                    className={cn(
                      smallTextSize,
                      'px-4 py-2 -mb-px border-b-2 transition-colors',
                      view === v.key
                        ? 'border-purple-600 dark:border-purple-600 text-purple-700 dark:text-purple-300 font-medium'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    )}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Active view */}
            <div className={cn(cardClass, 'overflow-x-auto')}>
              {view === 'cost' && <CostMatrixView data={data} columns={columns} lens={lens} />}
              {view === 'revenue' && <RevenueMatrixView data={data} columns={columns} lens={lens} />}
              {view === 'spending' && <SpendingMatrixView data={data} columns={columns} lens={lens} />}
              {view === 'overview' && <OverviewView data={data} lens={lens} />}
            </div>

            <p className={cn(secondaryText, 'text-xs')}>
              Data range: {data.from} → {data.to}. "Months" = months with both revenue and order
              data. Revenue-side food/drinks split is keyword-derived and approximate; total cost %
              is the most reliable figure.
            </p>
          </>
        )}

        {showCcModal && data && (
          <CcStartDateModal
            clients={data.clients}
            onClose={() => setShowCcModal(false)}
            onSaved={load}
          />
        )}
      </div>
    </AdminLayout>
  );
};

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
  <div className={cn(cardClass, 'p-3')}>
    <p className={cn(secondaryText, 'text-xs')}>{label}</p>
    <p className={cn(primaryText, 'text-lg', semiBoldText, 'mt-0.5')}>{value}</p>
  </div>
);

export default AdminAnalyticsPage;
